import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import picsioConfig from '../../../../../../config';
import localization from '../../../shared/strings';
import { setCookie } from '../../../shared/utils';
import { getDetailsPanelVisibility } from '../../details/helpers/getDetailsPanelDefaultState';
import {
  selectAggregatedDetailsData,
  selectAggregatedMetaLayer,
  selectAggregatedProductCoreFields,
  selectAggregatedProductInventoryFields,
  selectAggregatedProductMetaFields,
  selectAggregatedProductPricingFields,
  selectAggregatedProductSeoFields,
  selectAggregatedProductShippingFields,
  selectAggregatedVariantInventoryFields,
  selectAggregatedVariantMetaFields,
  selectAggregatedVariantPricingFields,
  selectAggregatedVariantShippingFields,
} from '../../../store/selectors/aggregatedProductDetailsSelectors';
import {
  selectProductSelectionContext,
  selectProductSelectionStats,
} from '../../../store/selectors/productSelectionSelectors';
import DetailsBlock from '../../DetailsBlock';
import ProductLayerMetaRow from '../ProductLayerMetaRow';
import { AggregatedBulkTargetContext } from './AggregatedBulkTargetContext';
import AggregatedBrand from './fields/AggregatedBrand';
import AggregatedCategory from './fields/AggregatedCategory';
import AggregatedDescription from './fields/AggregatedDescription';
import AggregatedKeywords from './fields/AggregatedKeywords';
import AggregatedProductName from './fields/AggregatedProductName';
import AggregatedProductType from './fields/AggregatedProductType';
import AggregatedProductStatus from './fields/AggregatedProductStatus';
import AggregatedSeoSection from './sections/AggregatedSeoSection';
import AggregatedPricingSection from './sections/AggregatedPricingSection';
import AggregatedInventorySection from './sections/AggregatedInventorySection';
import AggregatedShippingSection from './sections/AggregatedShippingSection';
import AggregatedOptionsSection from './sections/AggregatedOptionsSection';
import AggregatedMetaSection from './sections/AggregatedMetaSection';
import AggregatedVariantHeaders from './sections/AggregatedVariantHeaders';
import styles from './styles.module.scss';

const l10n = localization.PRODUCT_INFO;
const aggregatedL10n = l10n.aggregated;
const publishedReadOnlyTooltip = l10n.publishedReadOnlyFieldTooltip;
const PRODUCT_GROUP_BLOCK_NAME = 'aggregatedProductGroupVisibility';
const VARIANT_GROUP_BLOCK_NAME = 'aggregatedVariantGroupVisibility';

const isEntityGroupBlock = (blockName) => (
  blockName === PRODUCT_GROUP_BLOCK_NAME || blockName === VARIANT_GROUP_BLOCK_NAME
);

const AggregatedProductAttributesView = ({ mode }) => {
  const productCoreFields = useSelector(selectAggregatedProductCoreFields);
  const productSeoFields = useSelector(selectAggregatedProductSeoFields);
  const productPricingFields = useSelector(selectAggregatedProductPricingFields);
  const productInventoryFields = useSelector(selectAggregatedProductInventoryFields);
  const productShippingFields = useSelector(selectAggregatedProductShippingFields);
  const productMetaFields = useSelector(selectAggregatedProductMetaFields);
  const variantPricingFields = useSelector(selectAggregatedVariantPricingFields);
  const variantInventoryFields = useSelector(selectAggregatedVariantInventoryFields);
  const variantShippingFields = useSelector(selectAggregatedVariantShippingFields);
  const variantMetaFields = useSelector(selectAggregatedVariantMetaFields);
  const aggregatedData = useSelector(selectAggregatedDetailsData);
  const metaLayer = useSelector(selectAggregatedMetaLayer);
  const selectionStats = useSelector(selectProductSelectionStats);
  const selectionContext = useSelector(selectProductSelectionContext);
  const [panelsVisibility, setPanelsVisibility] = useState(getDetailsPanelVisibility());

  const readOnly = mode === 'published';
  const readOnlyTooltip = readOnly ? publishedReadOnlyTooltip : null;
  const isMasterMode = mode === 'master';
  const isListingMode = mode === 'draft' || mode === 'published';
  const channelId = selectionContext?.channelId || aggregatedData?.channelId || null;
  const hasProducts = selectionStats.products > 0;
  const hasVariants = selectionStats.variants > 0;
  const isMixedEntitySelection = hasProducts && hasVariants;
  const listingProductTrackedLocked = isListingMode && Boolean(aggregatedData?.productsHaveVariants);

  const toggleVisibility = useCallback((title) => {
    const detailsPanelVisibility = { ...panelsVisibility };
    const current = isEntityGroupBlock(title)
      ? (detailsPanelVisibility[title] ?? false)
      : detailsPanelVisibility[title];
    detailsPanelVisibility[title] = !current;
    setCookie('picsio.detailsPanelVisibility', JSON.stringify(detailsPanelVisibility));
    setPanelsVisibility(detailsPanelVisibility);
  }, [panelsVisibility]);

  const entityGroupVisibility = {
    ...panelsVisibility,
    [PRODUCT_GROUP_BLOCK_NAME]: panelsVisibility[PRODUCT_GROUP_BLOCK_NAME] ?? false,
    [VARIANT_GROUP_BLOCK_NAME]: panelsVisibility[VARIANT_GROUP_BLOCK_NAME] ?? false,
  };

  const updatedAtMax = productCoreFields?.updatedAtMax || null;
  const timeKind = mode === 'published' ? 'published' : 'updated';

  const renderProductMetaSection = (options = {}) => (
    <If condition={picsioConfig.isPim}>
      <AggregatedMetaSection
        metaFields={productMetaFields}
        attributeScope="product"
        layer={metaLayer}
        channelId={channelId}
        readOnly={readOnly}
        readOnlyTooltip={readOnlyTooltip}
        detailsPanelVisibility={panelsVisibility}
        toggleVisibility={toggleVisibility}
        blockName="productMetaVisibility"
        blockTitle={options.blockTitle}
        dataQa={options.dataQa}
      />
      <If condition={options.showTrailingBreak}>
        <br />
      </If>
    </If>
  );

  const renderVariantMetaSection = (options = {}) => (
    <If condition={picsioConfig.isPim}>
      <AggregatedMetaSection
        metaFields={variantMetaFields}
        attributeScope="variant"
        layer={metaLayer}
        channelId={channelId}
        readOnly={readOnly}
        readOnlyTooltip={readOnlyTooltip}
        detailsPanelVisibility={panelsVisibility}
        toggleVisibility={toggleVisibility}
        blockName="variantMetaVisibility"
        blockTitle={options.blockTitle || aggregatedL10n.variantCustomAttributes}
        dataQa={options.dataQa || 'aggregatedVariantCustomFields'}
      />
      <If condition={options.showTrailingBreak}>
        <br />
      </If>
    </If>
  );

  const renderProductSections = () => {
    const productStatusControl = (
      <AggregatedProductStatus
        statusField={productCoreFields?.status}
        includeArchived={isMasterMode}
        readOnly={readOnly}
        readOnlyTooltip={readOnlyTooltip}
      />
    );

    return (
      <AggregatedBulkTargetContext.Provider value="product">
        <ProductLayerMetaRow
          updatedAt={updatedAtMax}
          timeKind={timeKind}
          statusLabel={l10n.productStatusLabel}
          statusControl={productStatusControl}
        >
        <DetailsBlock
          dataQa="aggregatedProductCore"
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productCoreVisibility"
          blockTitle="Core"
        >
          <AggregatedProductName
            field={productCoreFields?.title}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
          />
          <AggregatedBrand
            field={productCoreFields?.vendor}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
          />
          <AggregatedProductType
            field={productCoreFields?.productType}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
          />
          <If condition={picsioConfig.isPim}>
            <AggregatedCategory
              field={productCoreFields?.category}
              readOnly={readOnly}
              readOnlyTooltip={readOnlyTooltip}
            />
          </If>
          <AggregatedDescription
            field={productCoreFields?.descriptionHtml}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
          />
          <br />
          <AggregatedKeywords
            field={productCoreFields?.keywords}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
          />
        </DetailsBlock>
        </ProductLayerMetaRow>
        <br />
        <AggregatedSeoSection
          readOnly={readOnly}
          readOnlyTooltip={readOnlyTooltip}
          seoFields={productSeoFields}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productSeoVisibility"
          blockTitle={l10n.seo.sectionTitle}
        />
        <br />
        <If condition={!isMixedEntitySelection}>
          <AggregatedOptionsSection
            readOnly={readOnly || isListingMode}
            readOnlyTooltip={readOnlyTooltip}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productOptionsVisibility"
          />
          <br />
        </If>
        <AggregatedPricingSection
          pricingFields={productPricingFields}
          readOnly={readOnly}
          readOnlyTooltip={readOnlyTooltip}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productPricingVisibility"
        />
        <br />
        <AggregatedInventorySection
          inventoryFields={productInventoryFields}
          showTracked={Boolean(productInventoryFields?.tracked)}
          readOnly={readOnly || listingProductTrackedLocked}
          readOnlyTooltip={listingProductTrackedLocked && !readOnly
            ? l10n.inventory.trackedIgnoredWhenVariantsTooltip
            : readOnlyTooltip}
          skuLabel="SKU"
          inlineSkuBarcode
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productInventoryVisibility"
        />
        <br />
        <AggregatedShippingSection
          shippingFields={productShippingFields}
          readOnly={readOnly}
          readOnlyTooltip={readOnlyTooltip}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productShippingVisibility"
        />
        <br />
        {renderProductMetaSection({ showTrailingBreak: true })}
      </AggregatedBulkTargetContext.Provider>
    );
  };

  const renderVariantOnlySections = () => (
    <AggregatedBulkTargetContext.Provider value="variant">
      <AggregatedVariantHeaders />
      <AggregatedPricingSection
        pricingFields={variantPricingFields}
        readOnly={readOnly}
        readOnlyTooltip={readOnlyTooltip}
        detailsPanelVisibility={panelsVisibility}
        toggleVisibility={toggleVisibility}
        blockName="variantPricingVisibility"
        blockTitle="Pricing"
      />
      <br />
      <AggregatedInventorySection
        inventoryFields={variantInventoryFields}
        showTracked={Boolean(variantInventoryFields?.tracked)}
        readOnly={readOnly}
        readOnlyTooltip={readOnlyTooltip}
        skuLabel={l10n.variantSku}
        inlineSkuBarcode
        detailsPanelVisibility={panelsVisibility}
        toggleVisibility={toggleVisibility}
        blockName="variantInventoryVisibility"
        blockTitle={l10n.skuCodesBlockTitle}
      />
      <br />
      <AggregatedShippingSection
        shippingFields={variantShippingFields}
        readOnly={readOnly}
        readOnlyTooltip={readOnlyTooltip}
        useVariantPreset
        detailsPanelVisibility={panelsVisibility}
        toggleVisibility={toggleVisibility}
        blockName="variantShippingVisibility"
        blockTitle="Shipping"
      />
      <br />
      {renderVariantMetaSection({
        blockTitle: l10n.customAttributes,
        showTrailingBreak: true,
      })}
    </AggregatedBulkTargetContext.Provider>
  );

  return (
    <div className={styles.attributesRoot}>
      <If condition={hasProducts}>
        <Choose>
          <When condition={isMixedEntitySelection}>
            <DetailsBlock
              dataQa="aggregatedProductAttributes"
              additionalClass={styles.entityGroup}
              detailsPanelVisibility={entityGroupVisibility}
              toggleVisibility={toggleVisibility}
              blockName={PRODUCT_GROUP_BLOCK_NAME}
              blockTitle={aggregatedL10n.productAttributesTitle}
            >
              {renderProductSections()}
            </DetailsBlock>
          </When>
          <Otherwise>
            {renderProductSections()}
          </Otherwise>
        </Choose>
      </If>

      <If condition={hasVariants}>
        <Choose>
          <When condition={isMixedEntitySelection}>
            <DetailsBlock
              dataQa="aggregatedVariantAttributes"
              detailsPanelVisibility={entityGroupVisibility}
              toggleVisibility={toggleVisibility}
              blockName={VARIANT_GROUP_BLOCK_NAME}
              blockTitle={aggregatedL10n.variantAttributesTitle}
            >
              {renderVariantOnlySections()}
            </DetailsBlock>
          </When>
          <Otherwise>
            {renderVariantOnlySections()}
          </Otherwise>
        </Choose>
      </If>
    </div>
  );
};

AggregatedProductAttributesView.propTypes = {
  mode: PropTypes.oneOf(['master', 'draft', 'published']).isRequired,
};

export default AggregatedProductAttributesView;
